import "./ChatAssistance.scss";
import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { Button, Typography } from "@mui/material";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import { Breadcrumb } from "../../breadcrumb/components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { RagChatThread, type RagChatThreadHandle } from "./chat/RagChatThread";

/** Fills viewport below the app header without changing App shell layout. */
function useChatPageViewportHeight(containerRef: RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const sync = () => {
      const { top } = el.getBoundingClientRect();
      const height = Math.max(0, Math.round(window.innerHeight - top));
      el.style.setProperty("--chat-assistance-height", `${height}px`);
    };

    sync();
    window.addEventListener("resize", sync);
    const ro = new ResizeObserver(sync);
    ro.observe(document.documentElement);

    return () => {
      window.removeEventListener("resize", sync);
      ro.disconnect();
      el.style.removeProperty("--chat-assistance-height");
    };
  }, [containerRef]);
}

export const ChatAssistance = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<RagChatThreadHandle>(null);
  useChatPageViewportHeight(containerRef);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div ref={containerRef} className="chat-assistance-container">
      <div className="chat-assistance-page-header">
        <div className="chat-assistance-content-rail">
          <Breadcrumb
            pathList={[
              { name: t("opva.chatAssistance"), path: "chat-assistance" },
            ]}
          />
          <div className="chat-assistance-title-row">
            <Typography variant="h3" className="chat-assistance-page-title">
              {t("opva.chatAssistance")}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              startIcon={<AddCommentOutlinedIcon />}
              onClick={() => threadRef.current?.startNewChat()}
              className="chat-assistance-new-chat-btn"
            >
              {t("opva.chatAssistanceNewChat")}
            </Button>
          </div>
        </div>
      </div>

      <div className="chat-assistance-chat-stack">
        <RagChatThread ref={threadRef} />
      </div>
    </div>
  );
};
